import { Routes, Route, Navigate } from 'react-router-dom';
import { LazyMotion, domAnimation } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './components/theme/theme-provider';
import { Toaster } from './components/ui/sonner';
import { AppShell } from './components/layout/AppShell';
import { AdministrationLayout } from './components/administration.layout';
import { BillingLayout } from './components/billing.layout';
import { SubscriptionRequiredCover } from './components/subscription/SubscriptionRequiredCover';
import { useLocaleSync } from './i18n/useLocaleSync';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import ProjectPage from './pages/Project';
import ProjectWorkflowPage from './pages/ProjectWorkflow';
import ProjectsPage from './pages/Projects';
import SessionPage from './pages/Session';
import GiftAgentPage from './pages/GiftAgentPage';
import SharedSessionPage from './pages/SharedSession';
import SharedGiftAgentPage from './pages/SharedGiftAgent';
import SessionsPage from './pages/Sessions';
import SettingsPage from './pages/Settings';
import DatabasesPage from './pages/Databases';
import TemplateMarketplacePage from './pages/TemplateMarketplace';
import RegistryPage from './pages/Registry';
import TalkPage from './pages/Talk';
import TalkSessionPage from './pages/TalkSession';
import AdminPage from './pages/Admin';
import AdminSessionsPage from './pages/AdminSessions';
import AdminProjectsPage from './pages/AdminProjects';
import AdminCreditsPage from './pages/AdminCredits';
import AdminModelMappingsPage from './pages/AdminModelMappings';
import AdminApiProvidersPage from './pages/AdminApiProviders';
import AdminProviderPluginsPage from './pages/AdminProviderPlugins';
import AdminResourcePlansPage from './pages/AdminResourcePlans';
import AdminCreditPlansPage from './pages/AdminCreditPlans';
import AdminCouponsPage from './pages/AdminCoupons';
import AdminTemplateCatalogPage from './pages/AdminTemplateCatalog';
import AdminRuntimeSettingsPage from './pages/AdminRuntimeSettings';
import AdminTeamAiSkillsPage from './pages/AdminTeamAiSkills';
import AdminCustomDomainSettingsPage from './pages/AdminCustomDomainSettings';
import AdminCustomDomainsPage from './pages/AdminCustomDomains';
import AdminAuditEventsPage from './pages/AdminAuditEvents';
import UsagePage from './pages/Usage';
import SubscriptionPage from './pages/Subscription';
import PaymentsPage from './pages/Payments';
import UsageHistoryPage from './pages/usage-history.page';
import PendingApprovalPage from './pages/PendingApproval';
import NotFoundPage from './pages/NotFoundPage';

function LoadingGate() {
  const { t } = useTranslation('common');
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      aria-label={t('loading')}
    >
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingGate />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function ApprovedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isApproved } = useAuth();

  if (loading) return <LoadingGate />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isApproved) return <Navigate to="/pending" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <LoadingGate />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  useLocaleSync();

  return (
    <LazyMotion features={domAnimation}>
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/share/session/:sessionId"
            element={<SharedSessionPage />}
          />
          <Route
            path="/share/agent/:sessionId"
            element={<SharedGiftAgentPage />}
          />
          <Route
            path="/pending"
            element={
              <ProtectedRoute>
                <PendingApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/:sessionId"
            element={
              <ApprovedRoute>
                <GiftAgentPage />
              </ApprovedRoute>
            }
          />
          <Route
            element={
              <ApprovedRoute>
                <AppShell />
              </ApprovedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:projectId" element={<ProjectPage />} />
            <Route
              path="projects/workflow/:projectId"
              element={<ProjectWorkflowPage />}
            />
            <Route path="talk" element={<TalkPage />} />
            <Route path="talk/:sessionId" element={<TalkSessionPage />} />
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="sessions/:sessionId" element={<SessionPage />} />
            <Route path="databases" element={<DatabasesPage />} />
            <Route path="templates" element={<TemplateMarketplacePage />} />
            <Route path="registry" element={<RegistryPage />} />
            <Route path="billing" element={<BillingLayout />}>
              <Route index element={<Navigate to="usage" replace />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="history" element={<UsageHistoryPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="payments" element={<PaymentsPage />} />
            </Route>
            <Route path="settings" element={<SettingsPage />} />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdministrationLayout />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="users" replace />} />
              <Route path="users" element={<AdminPage />} />
              <Route
                path="operations"
                element={<Navigate to="projects" replace />}
              />
              <Route
                path="operations/projects"
                element={<AdminProjectsPage />}
              />
              <Route
                path="operations/sessions"
                element={<AdminSessionsPage />}
              />
              <Route
                path="credits"
                element={<Navigate to="overview" replace />}
              />
              <Route path="credits/overview" element={<AdminCreditsPage />} />
              <Route path="credits/plans" element={<AdminCreditPlansPage />} />
              <Route
                path="credits/resources"
                element={<AdminResourcePlansPage />}
              />
              <Route path="credits/coupons" element={<AdminCouponsPage />} />
              <Route
                path="platform"
                element={<Navigate to="templates" replace />}
              />
              <Route
                path="ai-config/providers"
                element={<AdminApiProvidersPage />}
              />
              <Route
                path="ai-config/provider-plugins"
                element={<AdminProviderPluginsPage />}
              />
              <Route
                path="ai-config/models"
                element={<AdminModelMappingsPage />}
              />
              <Route
                path="platform/templates"
                element={<AdminTemplateCatalogPage />}
              />
              <Route
                path="ai-config/runtime-settings"
                element={<AdminRuntimeSettingsPage />}
              />
              <Route
                path="ai-config/team-ai-skills"
                element={<AdminTeamAiSkillsPage />}
              />
              <Route
                path="platform/domains"
                element={<AdminCustomDomainsPage />}
              />
              <Route
                path="platform/domain-settings"
                element={<AdminCustomDomainSettingsPage />}
              />
              <Route path="platform/audit" element={<AdminAuditEventsPage />} />
              <Route
                path="sessions"
                element={<Navigate to="/admin/operations/sessions" replace />}
              />
              <Route
                path="projects"
                element={<Navigate to="/admin/operations/projects" replace />}
              />
              <Route
                path="model-mappings"
                element={<Navigate to="/admin/ai-config/models" replace />}
              />
              <Route
                path="api-providers"
                element={<Navigate to="/admin/ai-config/providers" replace />}
              />
              <Route
                path="resource-plans"
                element={<Navigate to="/admin/credits/resources" replace />}
              />
              <Route
                path="credit-plans"
                element={<Navigate to="/admin/credits/plans" replace />}
              />
              <Route
                path="coupons"
                element={<Navigate to="/admin/credits/coupons" replace />}
              />
              <Route
                path="template-catalog"
                element={<Navigate to="/admin/platform/templates" replace />}
              />
              <Route
                path="runtime-settings"
                element={
                  <Navigate to="/admin/ai-config/runtime-settings" replace />
                }
              />
              <Route
                path="team-ai-skills"
                element={
                  <Navigate to="/admin/ai-config/team-ai-skills" replace />
                }
              />
              <Route
                path="custom-domain-settings"
                element={
                  <Navigate to="/admin/platform/domain-settings" replace />
                }
              />
              <Route
                path="custom-domains"
                element={<Navigate to="/admin/platform/domains" replace />}
              />
              <Route
                path="audit-events"
                element={<Navigate to="/admin/platform/audit" replace />}
              />
            </Route>
            <Route
              path="settings/preferences"
              element={<Navigate to="/settings" replace />}
            />
            <Route
              path="settings/billing"
              element={<Navigate to="/billing/usage" replace />}
            />
            <Route
              path="settings/billing/usage"
              element={<Navigate to="/billing/usage" replace />}
            />
            <Route
              path="settings/billing/history"
              element={<Navigate to="/billing/history" replace />}
            />
            <Route
              path="settings/billing/subscription"
              element={<Navigate to="/billing/subscription" replace />}
            />
            <Route
              path="settings/billing/payments"
              element={<Navigate to="/billing/payments" replace />}
            />
            <Route
              path="settings/users"
              element={
                <AdminRoute>
                  <Navigate to="/admin/users" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/operations/projects"
              element={
                <AdminRoute>
                  <Navigate to="/admin/operations/projects" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/operations/sessions"
              element={
                <AdminRoute>
                  <Navigate to="/admin/operations/sessions" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/credits/overview"
              element={
                <AdminRoute>
                  <Navigate to="/admin/credits/overview" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/credits/plans"
              element={
                <AdminRoute>
                  <Navigate to="/admin/credits/plans" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/credits/resources"
              element={
                <AdminRoute>
                  <Navigate to="/admin/credits/resources" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/credits/coupons"
              element={
                <AdminRoute>
                  <Navigate to="/admin/credits/coupons" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/providers"
              element={
                <AdminRoute>
                  <Navigate to="/admin/ai-config/providers" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/models"
              element={
                <AdminRoute>
                  <Navigate to="/admin/ai-config/models" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/templates"
              element={
                <AdminRoute>
                  <Navigate to="/admin/platform/templates" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/runtime-settings"
              element={
                <AdminRoute>
                  <Navigate to="/admin/ai-config/runtime-settings" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/team-ai-skills"
              element={
                <AdminRoute>
                  <Navigate to="/admin/ai-config/team-ai-skills" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/domains"
              element={
                <AdminRoute>
                  <Navigate to="/admin/platform/domains" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/domain-settings"
              element={
                <AdminRoute>
                  <Navigate to="/admin/platform/domain-settings" replace />
                </AdminRoute>
              }
            />
            <Route
              path="settings/platform/audit"
              element={
                <AdminRoute>
                  <Navigate to="/admin/platform/audit" replace />
                </AdminRoute>
              }
            />
            <Route
              path="usage"
              element={<Navigate to="/billing/usage" replace />}
            />
            <Route
              path="subscription"
              element={<Navigate to="/billing/subscription" replace />}
            />
          <Route
            path="payments"
            element={<Navigate to="/billing/payments" replace />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        </Routes>
        <SubscriptionRequiredCover />
      </>
      <Toaster />
    </LazyMotion>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider defaultTheme="system">
        <AppRoutes />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
